import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { addonAPI, verticalAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';

/**
 * One vertical, as a form rather than a JSON textarea.
 *
 * The console could already author a vertical — by pasting the same shape as
 * `backend/verticals/*.json` into a <textarea>. That is fine for the engineer
 * who wrote the schema and unusable for anybody selling the product, which is
 * who decides what a vertical offers.
 *
 * Two rules this screen is built around:
 *
 *   The add-on list is not a copy. It comes from `/api/addons/for-vertical`,
 *   already resolved — exclusions applied, labels and prices overridden. The
 *   catalog used to live twice, in Python and in JavaScript, and the copies
 *   disagreed in production twice this month. This screen renders what the
 *   server says a tenant on this vertical would be offered; it does not decide.
 *
 *   The raw JSON stays. It is the escape hatch for every field the form does
 *   not cover, and removing it would be a regression for whoever authored the
 *   twelve shipped verticals.
 */
const TRI_STATE = ['enabled', 'trial', 'disabled'];

export default function SuperadminVerticalDetailPage({ verticalKey, onBack }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [definition, setDefinition] = useState(null);
  const [label, setLabel] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [resolved, setResolved] = useState(null);   // /api/addons/for-vertical
  const [problems, setProblems] = useState([]);
  const [showRaw, setShowRaw] = useState(false);
  const [rawJson, setRawJson] = useState('');
  // Authoring a brand-new add-on. Kept collapsed: adding one is rare next to
  // deciding which of the existing ones a vertical offers.
  const [moduleCatalog, setModuleCatalog] = useState([]);
  const [newFlag, setNewFlag] = useState('');
  const [newTerm, setNewTerm] = useState({ key: '', value: '' });
  const [newAddon, setNewAddon] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [def, addons] = await Promise.all([
        verticalAPI.getDefinition(verticalKey),
        // Never lets a catalog hiccup blank the whole screen: the definition is
        // what this page edits, the add-ons are what it displays.
        addonAPI.forVertical(verticalKey).catch(() => ({ data: null })),
      ]);
      const body = def?.data?.definition || {};
      setDefinition(body);
      setLabel(body.label || verticalKey);
      setIsActive(body.listed !== false);
      setRawJson(JSON.stringify(body, null, 2));
      setResolved(addons?.data || null);
      setModuleCatalog(def?.data?.module_catalog || []);
      try {
        const v = await addonAPI.validate(verticalKey);
        setProblems(v?.data?.problems || []);
      } catch {
        setProblems([]);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not load this vertical'));
    } finally {
      setLoading(false);
    }
  }, [verticalKey]);

  useEffect(() => { load(); }, [load]);

  const addonConfig = useMemo(
    () => (definition?.addons && typeof definition.addons === 'object' ? definition.addons : {}),
    [definition],
  );
  const excluded = useMemo(
    () => new Set(Array.isArray(addonConfig.excluded) ? addonConfig.excluded : []),
    [addonConfig],
  );

  const patch = (mutate) => setDefinition((prev) => {
    const next = mutate({ ...(prev || {}) });
    setRawJson(JSON.stringify(next, null, 2));
    return next;
  });

  const toggleAddon = (key, offer) => patch((next) => {
    const current = new Set(Array.isArray(next.addons?.excluded) ? next.addons.excluded : []);
    if (offer) current.delete(key); else current.add(key);
    return { ...next, addons: { ...(next.addons || {}), excluded: [...current] } };
  });

  const setFlag = (key, value) => patch((next) => ({
    ...next,
    feature_flags: { ...(next.feature_flags || {}), [key]: value },
  }));

  /** A module is enabled, deliberately hidden, or simply not part of this
   *  vertical. The old switch could only take one away - there was no way to
   *  add a module the vertical had never had without editing JSON by hand.
   *
   *  `hidden` is not the same as absent: it is how a vertical says "the platform
   *  sells this and we deliberately do not show it", which the engine uses when
   *  resolving a tenant's navigation.
   */
  const setModuleState = (key, state) => patch((next) => {
    const modules = next.modules || {};
    const on = new Set(Array.isArray(modules.enabled) ? modules.enabled : []);
    const off = new Set(Array.isArray(modules.hidden) ? modules.hidden : []);
    on.delete(key); off.delete(key);
    if (state === 'enabled') on.add(key);
    if (state === 'hidden') off.add(key);
    return { ...next, modules: { ...modules, enabled: [...on], hidden: [...off] } };
  });

  const addFlag = () => {
    const key = newFlag.trim();
    if (!key) return;
    patch((next) => ({ ...next, feature_flags: { ...(next.feature_flags || {}), [key]: 'enabled' } }));
    setNewFlag('');
  };

  const removeFlag = (key) => patch((next) => {
    const flags = { ...(next.feature_flags || {}) };
    delete flags[key];
    return { ...next, feature_flags: flags };
  });

  const setTerm = (key, value) => patch((next) => ({
    ...next, terminology: { ...(next.terminology || {}), [key]: value },
  }));

  const removeTerm = (key) => patch((next) => {
    const terms = { ...(next.terminology || {}) };
    delete terms[key];
    return { ...next, terminology: terms };
  });

  const addTerm = () => {
    const key = newTerm.key.trim();
    if (!key) return;
    setTerm(key, newTerm.value.trim() || key);
    setNewTerm({ key: '', value: '' });
  };

  const createAddon = async () => {
    const key = (newAddon?.key || '').trim().toLowerCase();
    if (!/^[a-z][a-z0-9_]{1,62}$/.test(key)) {
      toast.error('A key is lowercase letters, digits and underscores, starting with a letter');
      return;
    }
    setCreating(true);
    try {
      await addonAPI.save(key, {
        key,
        label: (newAddon.label || '').trim() || key,
        description: newAddon.description || '',
        // Inherits this vertical's suite by default: an add-on authored from a
        // vertical's page is almost always for that vertical's industry, and a
        // cross-vertical one is the deliberate choice.
        suite: newAddon.crossVertical ? null : (resolved?.industry_suite || null),
        grants_module: Boolean(newAddon.grantsModule),
        monthly: newAddon.monthly ? Number(newAddon.monthly) : null,
        yearly: newAddon.yearly ? Number(newAddon.yearly) : null,
        is_active: true,
      });
      toast.success(`${newAddon.label || key} added to the catalog`);
      setNewAddon(null);
      await load();
    } catch (error) {
      // The server refuses a module-granting key that is not a module, and a
      // suite no vertical belongs to. Both messages say what to do instead.
      toast.error(getErrorMessage(error, 'Could not add that add-on'));
    } finally {
      setCreating(false);
    }
  };

  const save = async () => {
    let body = definition;
    if (showRaw) {
      try {
        body = JSON.parse(rawJson);
      } catch {
        toast.error('That is not valid JSON');
        return;
      }
    }
    setSaving(true);
    try {
      await verticalAPI.saveDefinition(verticalKey, {
        key: verticalKey,
        label: label.trim() || verticalKey,
        is_active: isActive,
        definition: { ...body, label: label.trim() || verticalKey },
      });
      toast.success(`${label || verticalKey} saved`);
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not save this vertical'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading {verticalKey}…</div>;
  }

  const offered = resolved?.addons || [];
  const withheld = resolved?.withheld || [];
  const flags = definition?.feature_flags || {};
  const terminology = definition?.terminology || {};
  const enabledModules = new Set(definition?.modules?.enabled || []);
  const hiddenModules = new Set(definition?.modules?.hidden || []);
  // The platform's catalog, plus anything this vertical names that the catalog
  // does not - a stale key is worth showing rather than hiding, since it is
  // exactly what the save will refuse.
  const allModules = [...new Set([...moduleCatalog, ...enabledModules, ...hiddenModules])].sort();

  return (
    <div className="space-y-6 p-1">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button type="button" onClick={onBack}
            className="text-sm text-muted-foreground hover:text-foreground">← Verticals</button>
          <h2 className="mt-1 text-2xl font-semibold text-foreground">{label || verticalKey}</h2>
          <p className="text-sm text-muted-foreground">
            <code>{verticalKey}</code>
            {resolved?.industry_suite ? <> · {resolved.industry_suite} suite</> : <> · cross-vertical</>}
            {' · '}{enabledModules.size} modules · {offered.length} add-ons on offer
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowRaw((v) => !v)}>
            {showRaw ? 'Form' : 'Advanced (JSON)'}
          </Button>
          <Button size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save vertical'}
          </Button>
        </div>
      </div>

      {problems.length > 0 && (
        <Card className="border-amber-500/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-amber-700 dark:text-amber-500">
              This vertical would offer something it can never grant
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm text-muted-foreground">
            {problems.map((p) => <p key={p}>· {p}</p>)}
          </CardContent>
        </Card>
      )}

      {showRaw ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Raw definition</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Same shape as <code>backend/verticals/*.json</code>. The form above covers
              modules, feature flags and add-ons; everything else — terminology, navigation,
              dashboards, workflows, marketplace — is edited here.
            </p>
            <Textarea rows={22} value={rawJson} onChange={(e) => setRawJson(e.target.value)}
              className="font-mono text-xs" />
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Identity</CardTitle></CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="vertical-label">Label</Label>
                <Input id="vertical-label" value={label} onChange={(e) => setLabel(e.target.value)} />
              </div>
              <div className="flex items-end gap-3 pb-1">
                <Switch id="vertical-listed" checked={isActive} onCheckedChange={setIsActive} />
                <Label htmlFor="vertical-listed" className="mb-1">
                  Offered at onboarding
                  <span className="block text-xs font-normal text-muted-foreground">
                    Unlisting hides it from the picker. Tenants already on it keep it.
                  </span>
                </Label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Add-ons on offer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                What a tenant on this vertical can be sold. Turning one off excludes it for
                this vertical only; the catalog is unchanged and any tenant already holding
                it keeps it — removing a paid add-on is a billing decision, not this screen&apos;s.
              </p>
              <div className="grid gap-2 md:grid-cols-2">
                {offered.map((addon) => (
                  <label key={addon.key}
                    className="flex items-start justify-between gap-3 rounded-xl border border-border bg-card px-3 py-2">
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground">{addon.label}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        <code>{addon.key}</code>
                        {addon.grants_module ? ' · grants a module' : ' · feature'}
                        {addon.default_pricing?.monthly ? ` · ₹${addon.default_pricing.monthly}/mo` : ''}
                      </span>
                    </span>
                    <Switch
                      checked={!excluded.has(addon.key)}
                      onCheckedChange={(on) => toggleAddon(addon.key, on)}
                    />
                  </label>
                ))}
                {offered.length === 0 && (
                  <p className="text-sm text-muted-foreground">No add-ons resolve for this vertical.</p>
                )}
              </div>

              {newAddon ? (
                <div className="space-y-3 rounded-xl border border-primary/40 bg-primary/5 p-3">
                  <p className="text-sm font-medium text-foreground">New add-on</p>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor="addon-key">Key</Label>
                      <Input id="addon-key" placeholder="concierge_onboarding"
                        value={newAddon.key || ''}
                        onChange={(e) => setNewAddon((p) => ({ ...p, key: e.target.value }))} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="addon-label">Label</Label>
                      <Input id="addon-label" placeholder="Concierge Onboarding"
                        value={newAddon.label || ''}
                        onChange={(e) => setNewAddon((p) => ({ ...p, label: e.target.value }))} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="addon-monthly">Monthly price</Label>
                      <Input id="addon-monthly" inputMode="decimal" value={newAddon.monthly || ''}
                        onChange={(e) => setNewAddon((p) => ({ ...p, monthly: e.target.value }))} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="addon-yearly">Yearly price</Label>
                      <Input id="addon-yearly" inputMode="decimal" value={newAddon.yearly || ''}
                        onChange={(e) => setNewAddon((p) => ({ ...p, yearly: e.target.value }))} />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Switch checked={Boolean(newAddon.crossVertical)}
                      onCheckedChange={(v) => setNewAddon((p) => ({ ...p, crossVertical: v }))} />
                    Sell to every vertical, not just {resolved?.industry_suite || 'this one'}
                  </label>
                  <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Switch checked={Boolean(newAddon.grantsModule)}
                      onCheckedChange={(v) => setNewAddon((p) => ({ ...p, grantsModule: v }))} />
                    Grants a module of the same name
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Leave that off unless a module of exactly this key already exists — the
                    server refuses a module-granting add-on that names one that does not,
                    because it would be sold and never granted.
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={createAddon} disabled={creating}>
                      {creating ? 'Adding…' : 'Add to catalog'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setNewAddon(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setNewAddon({})}>
                  Add an add-on
                </Button>
              )}

              {withheld.length > 0 && (
                <details className="rounded-xl border border-border bg-muted/30 px-3 py-2">
                  <summary className="cursor-pointer text-sm text-muted-foreground">
                    {withheld.length} not available here, and why
                  </summary>
                  <div className="mt-2 space-y-1">
                    {withheld.map((row) => (
                      <p key={row.key} className="text-xs text-muted-foreground">
                        <code>{row.key}</code> — {row.reason}
                      </p>
                    ))}
                  </div>
                </details>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Terminology</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-muted-foreground">
                What this industry calls things. A retailer&apos;s employee is a Store
                Associate and their branch is a Store; the whole product reads back in
                these words.
              </p>
              {Object.entries(terminology).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <code className="w-40 shrink-0 truncate text-xs text-muted-foreground">{key}</code>
                  <Input value={value} onChange={(e) => setTerm(key, e.target.value)} />
                  <Button type="button" size="sm" variant="outline" onClick={() => removeTerm(key)}>
                    Remove
                  </Button>
                </div>
              ))}
              <div className="flex items-center gap-2 pt-1">
                <Input className="w-40 shrink-0" placeholder="employee"
                  value={newTerm.key}
                  onChange={(e) => setNewTerm((p) => ({ ...p, key: e.target.value }))} />
                <Input placeholder="Store Associate" value={newTerm.value}
                  onChange={(e) => setNewTerm((p) => ({ ...p, value: e.target.value }))} />
                <Button type="button" size="sm" variant="outline" onClick={addTerm}>Add</Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Feature flags</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {Object.keys(flags).length === 0 && (
                <p className="text-sm text-muted-foreground">None set.</p>
              )}
              {Object.entries(flags).map(([key, value]) => (
                <div key={key}
                  className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card px-3 py-2">
                  <code className="truncate text-sm text-foreground">{key}</code>
                  <div className="flex shrink-0 gap-1">
                    {TRI_STATE.map((state) => (
                      <Button key={state} type="button" size="sm"
                        variant={value === state ? 'default' : 'outline'}
                        onClick={() => setFlag(key, state)}>
                        {state}
                      </Button>
                    ))}
                    <Button type="button" size="sm" variant="outline" onClick={() => removeFlag(key)}>
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-2 pt-1">
                <Input placeholder="edu_research" value={newFlag}
                  onChange={(e) => setNewFlag(e.target.value)} />
                <Button type="button" size="sm" variant="outline" onClick={addFlag}>Add flag</Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Modules — {enabledModules.size} on, {hiddenModules.size} hidden
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-3 text-xs text-muted-foreground">
                Every module the platform sells, and what this vertical does with it.
                <strong className="font-medium"> On</strong> provisions it,
                <strong className="font-medium"> Hidden</strong> says the platform sells it
                and this industry deliberately does not show it, and
                <strong className="font-medium"> Off</strong> leaves it out entirely.
                A module switched off is withdrawn from every tenant on this vertical on
                their next request, so it is not a quick edit.
              </p>
              <div className="grid gap-1 sm:grid-cols-2">
                {allModules.map((key) => {
                  const state = enabledModules.has(key) ? 'enabled'
                    : hiddenModules.has(key) ? 'hidden' : 'off';
                  return (
                    <div key={key}
                      className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-2 py-1.5">
                      <code className="truncate text-xs text-foreground">{key}</code>
                      <div className="flex shrink-0 gap-1">
                        {['enabled', 'hidden', 'off'].map((option) => (
                          <Button key={option} type="button" size="sm"
                            variant={state === option ? 'default' : 'outline'}
                            className="h-6 px-2 text-[11px]"
                            onClick={() => setModuleState(key, option)}>
                            {option === 'enabled' ? 'On' : option === 'hidden' ? 'Hidden' : 'Off'}
                          </Button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
